import { Bell, Loader2 } from 'lucide-react'
import { usePushNotifications } from '../../hooks/use-push-notifications'

interface SeriesPushToggleProps {
  /**
   * Compact variant matches the small pill-style action buttons used in the
   * series detail header. The wide variant is used inside the post-creation card.
   */
  variant?: 'compact' | 'wide'
  onSubscribed?: () => void
}

/**
 * Toggle button that turns Web Push notifications on or off for the current device.
 * Hidden entirely on browsers that don't support push (e.g. iOS Safari outside of a
 * Home-Screen-installed PWA) so the UI doesn't promise something that can't be delivered.
 */
export function SeriesPushToggle({ variant = 'compact', onSubscribed }: SeriesPushToggleProps) {
  const { isSupported, permission, isSubscribed, isWorking, error, subscribe, unsubscribe } =
    usePushNotifications()

  if (!isSupported) return null

  const handleClick = async () => {
    if (isSubscribed) {
      await unsubscribe()
    } else {
      const ok = await subscribe()
      if (ok && onSubscribed) onSubscribed()
    }
  }

  const permanentlyDenied = !isSubscribed && permission === 'denied'

  if (variant === 'wide') {
    // The wide variant is a promotional prompt — only show it when the user hasn't
    // enabled notifications yet. We never want to push users to disable them.
    if (isSubscribed) return null

    const title = 'Get notified when a new episode is ready'
    const description = permanentlyDenied
      ? 'Notifications are blocked in your browser settings — enable them there to receive episode alerts.'
      : 'Enable push notifications and we’ll ping you the moment your next episode is ready to listen to.'

    return (
      <div className="flex items-start gap-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
          <Bell className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          {error && <p className="mt-2 text-xs text-danger-600">{error}</p>}
        </div>
        <button
          type="button"
          onClick={handleClick}
          disabled={isWorking || permanentlyDenied}
          className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
          {isWorking ? 'Working…' : 'Enable'}
        </button>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isWorking || permanentlyDenied}
      title={
        permanentlyDenied
          ? 'Notifications are blocked in your browser settings'
          : isSubscribed
            ? 'Disable episode notifications on this device'
            : 'Enable episode notifications on this device'
      }
      className={`flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
        isSubscribed
          ? 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100'
          : 'border-black/10 bg-white text-foreground hover:bg-black/5'
      }`}
    >
      {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
      <span>{isSubscribed ? 'Notifications on' : 'Notify me'}</span>
    </button>
  )
}
